import { useCallback, useState } from 'react';

export interface UseAsyncFormOptions {
  /** Invoked once the action resolves successfully. */
  onSuccess?: () => void;
  /** Message shown when a thrown value is not a recognized error. */
  errorFallback?: string;
  /** Maps a thrown value to a user-facing message (e.g. extractClerkError). */
  mapError?: (err: unknown) => string;
}

export interface UseAsyncFormReturn<TArgs extends unknown[]> {
  submit: (...args: TArgs) => Promise<void>;
  isSubmitting: boolean;
  error: string;
  reset: () => void;
  clearError: () => void;
}

const DEFAULT_ERROR_FALLBACK = 'Something went wrong. Please try again.';

/**
 * Wraps an async action with submit lifecycle state.
 *
 * submit clears the error, flips isSubmitting on, runs the action, and
 * guarantees isSubmitting returns to false. On rejection it derives a message
 * via mapError (falling back to errorFallback). onSuccess fires only when the
 * action resolves, leaving success-state flags to the caller.
 *
 * Per §TS.3.1: Function declaration for named export
 */
export function useAsyncForm<TArgs extends unknown[]>(
  action: (...args: TArgs) => Promise<void>,
  options: UseAsyncFormOptions = {}
): UseAsyncFormReturn<TArgs> {
  const { onSuccess, errorFallback = DEFAULT_ERROR_FALLBACK, mapError } = options;

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const clearError = useCallback(() => {
    setError('');
  }, []);

  const reset = useCallback(() => {
    setError('');
    setIsSubmitting(false);
  }, []);

  const submit = useCallback(
    async (...args: TArgs) => {
      setError('');
      setIsSubmitting(true);

      try {
        await action(...args);
        onSuccess?.();
      } catch (err) {
        const message = mapError ? mapError(err) : errorFallback;
        setError(message);
      } finally {
        setIsSubmitting(false);
      }
    },
    [action, onSuccess, errorFallback, mapError]
  );

  return { submit, isSubmitting, error, reset, clearError };
}
